import { message } from '@/components/Message';
import Modal from '@/components/Modal';
import { Page } from '@/models/common';
import { identity } from '@/utils/common';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { castDraft, Draft, nothing } from 'immer';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useMountedState } from 'react-use';
import { useImmer } from 'use-immer';
import api, { RequestMethod } from '.';
import { APIError, APIRequestParam } from './types';

const DEFAULT_LOADING = false;
const DEFAULT_LOADED = false;
const DEFAULT_PAYLOAD = undefined;
const DEFAULT_ERROR = null;

type SendParamsType = {
  data?: object;
  params?: object;
  pathVars?: object;
};

export type RequestParams<SendParams extends SendParamsType = SendParamsType> = Partial<APIRequestParam> & {
  method?: RequestMethod;

  data?: SendParams['data'];
  params?: SendParams['params'];
  pathVars?: SendParams['pathVars'];
};

export type RequestConfig = {
  /** 手动触发 */
  manual?: boolean;
};

/**
 * 发送请求
 * @param requestParams 请求配置
 * @param config hook 配置
 */

export const useRequest = <T, SendParams extends SendParamsType = SendParamsType>(
  requestParams: RequestParams<SendParams>,
  config: RequestConfig = {
    manual: false,
  }
) => {
  const mounted = useRef(true);
  const [status, updateStatus] = useState({
    loading: DEFAULT_LOADING,
    loaded: DEFAULT_LOADED,
    error: DEFAULT_ERROR as APIError,
    payload: DEFAULT_PAYLOAD as T,
  });

  const reset = useCallback(() => {
    updateStatus(prev => ({
      loaded: DEFAULT_LOADED,
      loading: DEFAULT_LOADING,
      error: DEFAULT_ERROR,
      payload: DEFAULT_PAYLOAD,
    }));
  }, [updateStatus]);

  const clearErrorAndLoading = useCallback(() => {
    updateStatus(prev => ({
      ...prev,
      loading: DEFAULT_LOADING,
      error: DEFAULT_ERROR,
    }));
  }, [updateStatus]);

  const send = async (overrideRequestParams?: RequestParams<SendParams> & { transformer?: (v: T) => T }) => {
    updateStatus(prev => ({
      ...prev,
      loading: true,
    }));

    overrideRequestParams = Object.assign({}, requestParams, overrideRequestParams);
    const { method = 'get', pathVars, transformer = identity, ...params } = overrideRequestParams;
    if (pathVars) {
      Object.entries(pathVars).forEach(([varName, varValue]) => {
        params.url = params.url.replace(`{${varName}}`, String(varValue));
      });
    }
    // 清除未替换的 pathVars
    params.url = params.url.replace(/\{.*?\}/g, '');

    try {
      const payload = await api[method]<T>(params);
      if (mounted.current) {
        const transformedPayload = transformer(payload);
        updateStatus(() => ({
          error: null,
          loading: false,
          loaded: true,
          payload: transformedPayload,
        }));
        return transformedPayload;
      }
    } catch (error) {
      if (mounted.current) {
        updateStatus(() => ({
          loading: false,
          error: error,
          loaded: false,
          payload: DEFAULT_PAYLOAD,
        }));
        throw error;
      }
    }
  };

  useEffect(() => {
    if (!config.manual) {
      send().then();
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const setPayload = useCallback(
    (payload: T) => {
      updateStatus(prev => ({
        ...prev,
        payload,
      }));
    },
    [updateStatus]
  );

  return {
    loaded: status.loaded,
    loading: status.loading,
    error: status.error,
    payload: status.payload,
    status,
    setPayload,
    send,
    reset,
    clearErrorAndLoading,
  };
};

export const useRequestImmer = <T, SendParams extends SendParamsType = SendParamsType>(
  requestParams: RequestParams<SendParams>,
  config: RequestConfig
) => {
  const { status, ...rest } = useRequest<T, SendParams>(requestParams, config);
  const [request, setRequest] = useImmer(status);

  useEffect(() => {
    setRequest(() => status);
  }, [status]);

  const setPayload = (updater: (draft: Draft<T>) => void | T) => {
    setRequest(draft => {
      const replacement = updater(draft.payload);
      if (replacement) {
        draft.payload = castDraft(replacement);
      }
    });
  };

  return {
    ...rest,
    ...request,
    payload: request.payload,
    setPayload: setPayload,
  };
};

export const useRequestDelete = (api: string) => {
  const { send } = useRequest(
    {
      method: 'delete',
    },
    { manual: true }
  );
  const requestDelete = useCallback(
    (id: string | number) =>
      new Promise<void>((resolve, reject) => {
        Modal.confirm({
          title: '确认删除吗？',
          icon: <ExclamationCircleOutlined />,
          content: '数据删除后不可恢复，请谨慎操作，并再次确认',
          okText: '删除',
          okButtonProps: { danger: true },
          cancelText: '取消',
          onOk() {
            const hide = message.loading('数据删除中..', 0);
            send({
              url: `${api}/${id}`,
              // url: `${api}/0`,
            })
              .then(() => {
                hide();
                message.success('删除成功！');
                resolve();
                // setActive(false);
              })
              .catch(error => {
                hide();
                message.error(`删除失败: ${error.msg}`);
                reject();
              });
          },
          onCancel() {
            // console.log('Cancel');
          },
        });
      }),
    // [setActive, setId]
    [api]
  );

  // useEffect(() => {
  //   if (loaded) {
  //     message.success('删除成功！');
  //     setActive(false);
  //   } else if (error) {
  //     message.error(`删除失败: ${error.msg}`);
  //   }
  // }, [loaded, error, setActive, id]);

  return [requestDelete] as [typeof requestDelete];
};

export const useRequestImmerWithInfiniteList = <
  T extends { list: any[]; page: Page },
  SendParams extends SendParamsType = SendParamsType,
>(
  requestParams: RequestParams<SendParams>,
  config: RequestConfig
) => {
  const { payload, reset, ...rest } = useRequest<T, SendParams>(requestParams, config);

  const [immerPayload, setImmerPayload] = useImmer<T>(payload);

  useEffect(() => {
    setImmerPayload(draft => {
      if (draft && draft.list) {
        draft.list.splice(draft.list.length, 0, ...payload.list);
        draft.page = payload.page;
      } else {
        return payload;
      }
    });
  }, [payload]);

  return {
    ...rest,
    payload: immerPayload,
    setPayload: setImmerPayload,
    reset: () => {
      reset();
      setImmerPayload(() => nothing as unknown as T);
    },
  };
};

/**
 * 分页信息全量查询
 */
export const useFullListRequest = <T, SendParams extends SendParamsType = SendParamsType>(
  requestParams: RequestParams<SendParams>,
  config: RequestConfig
) => {
  const { send, ...rest } = useRequest<{
    list: T[];
    page: Page;
  }>(requestParams, config);

  const [status, setStatus] = useImmer<{ loading: boolean; loaded: boolean; payload: T[] }>({
    loading: false,
    loaded: false,
    payload: [],
  });

  const fetch = useCallback(
    async (requestParams: Parameters<typeof send>[0] = {}) => {
      setStatus(draft => {
        draft.loading = true;
      });

      let result = [] as T[];
      let current = 1;

      while (true) {
        const rsp = await send(
          Object.assign({}, requestParams, {
            params: Object.assign({}, { page: current++, limit: 50 }, requestParams.params || {}),
          })
        );
        result = result.concat(rsp.list);
        if (!rsp.page || rsp.page.current >= rsp.page.pages) {
          break;
        }
      }

      setStatus(draft => {
        draft.loading = false;
        draft.loaded = true;
        draft.payload = castDraft(result);
      });

      return result;
    },
    [send, setStatus]
  );

  const setPayload = useCallback(
    (updator: (draft: T[]) => void | T[]) => {
      setStatus(draft => {
        const newState = updator(draft.payload as T[]);
        if (newState) {
          draft.payload = castDraft(newState);
        }
      });
    },
    [setStatus]
  );

  return {
    ...rest,
    loading: status.loading,
    loaded: status.loaded,
    payload: status.payload,
    send: fetch,
    setPayload,
  };
};

/**
 * useSafeState - 类似 useState，但在组件卸载后调用 setter 不会报错
 */
export function useSafeState<S>(initialState: S | (() => S)): [S, Dispatch<SetStateAction<S>>] {
  const [state, setState] = useState(initialState);
  const isMounted = useMountedState();

  const safeSetState: Dispatch<SetStateAction<S>> = value => {
    if (isMounted()) {
      setState(value);
    }
  };

  return [state, safeSetState];
}
